import { Controller } from "@hotwired/stimulus"
import { loadMapboxGL } from "../lib/mapbox_loader"

// Mapbox is lazy-loaded from CDN to reduce bundle size
export default class extends Controller {
  static targets = ["container", "lat", "lng", "radius", "radiusDisplay", "search"]
  static values = {
    lat: { type: Number, default: 33.4484 },
    lng: { type: Number, default: -112.0773 },
    radius: { type: Number, default: 10 }
  }

  connect() {
    this.map = null
    this.marker = null
    this.radiusHandle = null
    this.mapInitialized = false
    this.mapboxgl = null

    // Listen for the toggle event to initialize map when visible
    this.element.addEventListener('zone-type:radius-shown', () => {
      this.ensureMapInitialized()
    })

    // Check if already visible (editing an existing radius zone)
    if (!this.element.classList.contains('hidden')) {
      this.ensureMapInitialized()
    }
  }

  get brandColor() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--brand-500').trim() || '#3b82f6'
  }

  ensureMapInitialized() {
    if (this.mapInitialized) {
      if (this.map) {
        this.map.resize()
      }
      return
    }

    // Wait for container to be visible and have dimensions
    setTimeout(() => this.initializeMap(), 50)
  }

  async initializeMap() {
    if (!this.hasContainerTarget) return
    if (this.mapInitialized) return

    const rect = this.containerTarget.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) {
      setTimeout(() => this.initializeMap(), 100)
      return
    }

    // Load mapbox lazily
    this.mapboxgl = await loadMapboxGL()
    this.mapboxgl.accessToken = 'pk.eyJ1IjoiYWxldmVyZW56NTgiLCJhIjoiY21pZGozaTYwMDczZzJrb295bnhkY3JuOCJ9._JeXNOLxl5PhHINW4eIXUw'

    // Use existing values if present
    const lat = this.hasLatTarget && this.latTarget.value ? parseFloat(this.latTarget.value) : this.latValue
    const lng = this.hasLngTarget && this.lngTarget.value ? parseFloat(this.lngTarget.value) : this.lngValue
    const radius = this.hasRadiusTarget && this.radiusTarget.value ? parseFloat(this.radiusTarget.value) : this.radiusValue

    // Calculate appropriate zoom level based on radius
    const zoom = this.calculateZoomForRadius(radius)

    this.map = new this.mapboxgl.Map({
      container: this.containerTarget,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [lng, lat],
      zoom: zoom,
      pitch: 0,
      bearing: 0,
      antialias: true
    })

    this.mapInitialized = true

    // Add navigation controls
    this.map.addControl(new this.mapboxgl.NavigationControl(), 'top-right')

    // Add draggable center marker
    this.marker = new this.mapboxgl.Marker({ draggable: true, color: this.brandColor })
      .setLngLat([lng, lat])
      .addTo(this.map)

    // Update fields when marker is dragged
    this.marker.on('dragend', () => this.updateFromMarker())

    // Draw radius circle when map loads
    this.map.on('load', () => {
      this.addRadiusCircle(lat, lng, radius)
      this.addRadiusHandle(lat, lng, radius)
    })

    // Update circle when radius changes
    if (this.hasRadiusTarget) {
      this.radiusTarget.addEventListener('input', () => this.updateRadius())
    }
  }

  calculateZoomForRadius(radiusMiles) {
    if (radiusMiles <= 2) return 12
    if (radiusMiles <= 5) return 11
    if (radiusMiles <= 10) return 10
    if (radiusMiles <= 20) return 9
    if (radiusMiles <= 50) return 8
    return 7
  }

  updateFromMarker() {
    const lngLat = this.marker.getLngLat()

    if (this.hasLatTarget) {
      this.latTarget.value = lngLat.lat.toFixed(6)
    }
    if (this.hasLngTarget) {
      this.lngTarget.value = lngLat.lng.toFixed(6)
    }

    this.updateRadiusCircle()
    this.updateRadiusHandle()
  }

  updateRadius() {
    this.updateRadiusCircle()
    this.updateRadiusHandle()

    if (this.hasRadiusDisplayTarget && this.hasRadiusTarget) {
      this.radiusDisplayTarget.textContent = `${this.radiusTarget.value} miles`
    }
  }

  addRadiusCircle(lat, lng, radiusMiles) {
    const radiusMeters = radiusMiles * 1609.34
    const circle = this.createGeoJSONCircle([lng, lat], radiusMeters)

    this.map.addSource('radius-circle', {
      type: 'geojson',
      data: circle
    })

    this.map.addLayer({
      id: 'radius-circle-fill',
      type: 'fill',
      source: 'radius-circle',
      paint: {
        'fill-color': this.brandColor,
        'fill-opacity': 0.15
      }
    })

    this.map.addLayer({
      id: 'radius-circle-stroke',
      type: 'line',
      source: 'radius-circle',
      paint: {
        'line-color': this.brandColor,
        'line-width': 2
      }
    })
  }

  addRadiusHandle(lat, lng, radiusMiles) {
    const handlePos = this.calculateHandlePosition(lat, lng, radiusMiles)

    const el = document.createElement('div')
    el.className = 'radius-handle'
    el.style.cssText = `
      width: 16px;
      height: 16px;
      background: ${this.brandColor};
      border: 2px solid white;
      border-radius: 50%;
      cursor: ew-resize;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    `

    this.radiusHandle = new this.mapboxgl.Marker({ element: el, draggable: true })
      .setLngLat([handlePos.lng, handlePos.lat])
      .addTo(this.map)

    this.radiusHandle.on('drag', () => this.updateRadiusFromHandle())
    this.radiusHandle.on('dragend', () => this.updateRadiusFromHandle())
  }

  calculateHandlePosition(lat, lng, radiusMiles) {
    const km = radiusMiles * 1.60934
    const lngOffset = km / (111.320 * Math.cos(lat * Math.PI / 180))
    return { lat: lat, lng: lng + lngOffset }
  }

  updateRadiusHandle() {
    if (!this.radiusHandle) return

    const center = this.marker.getLngLat()
    const radiusMiles = this.hasRadiusTarget ? parseFloat(this.radiusTarget.value) || 10 : 10
    const handlePos = this.calculateHandlePosition(center.lat, center.lng, radiusMiles)
    this.radiusHandle.setLngLat([handlePos.lng, handlePos.lat])
  }

  updateRadiusFromHandle() {
    if (!this.radiusHandle) return

    const center = this.marker.getLngLat()
    const handle = this.radiusHandle.getLngLat()

    const latDiff = handle.lat - center.lat
    const lngDiff = handle.lng - center.lng

    const latKm = latDiff * 110.574
    const lngKm = lngDiff * 111.320 * Math.cos(center.lat * Math.PI / 180)
    const distanceKm = Math.sqrt(latKm * latKm + lngKm * lngKm)
    const distanceMiles = distanceKm / 1.60934

    const newRadius = Math.max(1, Math.min(100, distanceMiles.toFixed(1)))

    if (this.hasRadiusTarget) {
      this.radiusTarget.value = newRadius
    }

    if (this.hasRadiusDisplayTarget) {
      this.radiusDisplayTarget.textContent = `${newRadius} miles`
    }

    this.updateRadiusCircle()
  }

  updateRadiusCircle() {
    if (!this.map || !this.map.getSource('radius-circle')) return

    const lngLat = this.marker.getLngLat()
    const radiusMiles = this.hasRadiusTarget ? parseFloat(this.radiusTarget.value) || 10 : 10
    const radiusMeters = radiusMiles * 1609.34

    const circle = this.createGeoJSONCircle([lngLat.lng, lngLat.lat], radiusMeters)
    this.map.getSource('radius-circle').setData(circle)
  }

  createGeoJSONCircle(center, radiusMeters, points = 64) {
    const coords = { latitude: center[1], longitude: center[0] }
    const km = radiusMeters / 1000
    const ret = []
    const distanceX = km / (111.320 * Math.cos(coords.latitude * Math.PI / 180))
    const distanceY = km / 110.574

    for (let i = 0; i < points; i++) {
      const theta = (i / points) * (2 * Math.PI)
      const x = distanceX * Math.cos(theta)
      const y = distanceY * Math.sin(theta)
      ret.push([coords.longitude + x, coords.latitude + y])
    }
    ret.push(ret[0])

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [ret]
      }
    }
  }

  async searchLocation(event) {
    if (event) event.preventDefault()
    if (!this.hasSearchTarget || !this.searchTarget.value.trim()) return

    const query = this.searchTarget.value.trim()

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?country=US&access_token=${this.mapboxgl.accessToken}`
      )
      const data = await response.json()

      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center

        // Move marker to new location
        this.marker.setLngLat([lng, lat])

        // Update hidden fields
        if (this.hasLatTarget) {
          this.latTarget.value = lat.toFixed(6)
        }
        if (this.hasLngTarget) {
          this.lngTarget.value = lng.toFixed(6)
        }

        // Update circle and handle
        this.updateRadiusCircle()
        this.updateRadiusHandle()

        // Fly to new location
        const radius = this.hasRadiusTarget ? parseFloat(this.radiusTarget.value) || 10 : 10
        this.map.flyTo({
          center: [lng, lat],
          zoom: this.calculateZoomForRadius(radius),
          duration: 1000
        })
      } else {
        if (window.showToast) {
          window.showToast('Location not found. Try a different search term.', 'warning')
        } else {
          alert('Location not found. Try a different search term.')
        }
      }
    } catch (error) {
      if (window.showToast) {
        window.showToast('Error searching for location. Please try again.', 'error')
      } else {
        alert('Error searching for location. Please try again.')
      }
    }
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }
}
